import { appWithTranslation } from 'next-i18next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { GoogleAnalyticsProvider } from 'react-ga4-gtag'

import { FACEBOOK_PIXEL, GOOGLE_GA4 } from '@goodfellas/environment'
import '@goodfellas/feedback-card-ui/styles.css'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { Layout } from '../components/Layout'
import { AuthProvider } from '../contexts/AuthContext'
import { useAttribution } from '../hooks/useAttribution'
import nextConfig from '../next-i18next.config.js'
import { FirebaseAnalyticsProvider } from '../providers/firebaseAnalytics'
import { PostHogProvider } from '../providers/postHog'
import './globals.css'

// Rotas que usam o chrome do novo design (nav/footer próprios) e não devem
// receber o Header/Footer legados: a landing e as páginas de deep link que
// espelham as rotas do app (AASA/assetlinks).
const STANDALONE_PREFIXES = [
  '/trainings',
  '/training',
  '/challenges',
  '/cartel',
  '/ranking',
  '/chat',
  '/notifications',
  '/my-gym',
  '/evaluation',
  '/profile',
  '/gym', // singular (rota do app); /gyms continua com o Layout legado
  '/termos', // páginas legais renderizam o próprio chrome (LegalPage)
  '/privacidade',
]

function isStandalone(slug: string): boolean {
  if (slug === '/') return true
  return STANDALONE_PREFIXES.some(
    (prefix) => slug === prefix || slug.startsWith(`${prefix}/`),
  )
}

// Inner component that can use AuthContext
function AppContent({
  Component,
  pageProps,
  slug,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageProps: any
  slug: string
}) {
  // Captura a origem (?ref=/utm) do link de entrada para atribuição (#821).
  useAttribution()

  // The admin dashboard has its own chrome (sidebar), and the new-design
  // pages (landing + deep links) render their own nav/footer.
  const isDashboard = slug.startsWith('/dashboard')

  if (isDashboard || isStandalone(slug)) {
    return (
      <GoogleAnalyticsProvider measurementId={GOOGLE_GA4}>
        <Component {...pageProps} />
      </GoogleAnalyticsProvider>
    )
  }

  return (
    <Layout slug={slug}>
      <GoogleAnalyticsProvider measurementId={GOOGLE_GA4}>
        <Component {...pageProps} />
      </GoogleAnalyticsProvider>
    </Layout>
  )
}

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const slug = router.pathname

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
          },
        },
      }),
  )

  React.useEffect(() => {
    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        ReactPixel.init(FACEBOOK_PIXEL) // facebookPixelId
        ReactPixel.pageView()
        router.events.on('routeChangeComplete', () => {
          ReactPixel.pageView()
        })
      })
  }, [router.events])

  return (
    <>
      <Head>
        <title>Goodfellas — O mundo vira o seu tatame</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta charSet="utf-8" />

        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="images/favicons/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="images/favicons/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="images/favicons/favicon-16x16.png"
        />
        <link rel="manifest" href="images/favicons/site.webmanifest" />
        <link
          rel="mask-icon"
          href="images/favicons/safari-pinned-tab.svg"
          color="#5bbad5"
        />
        <link rel="shortcut icon" href="images/favicons/favicon.ico" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta
          name="msapplication-config"
          content="images/favicons/browserconfig.xml"
        />
        <meta name="theme-color" content="#EC1B2E" />
      </Head>
      <QueryClientProvider client={queryClient}>
        <PostHogProvider>
          <FirebaseAnalyticsProvider>
            <AuthProvider>
              <AppContent
                Component={Component}
                pageProps={pageProps}
                slug={slug}
              />
            </AuthProvider>
          </FirebaseAnalyticsProvider>
        </PostHogProvider>
      </QueryClientProvider>
    </>
  )
}

export default appWithTranslation(App, nextConfig)
